import { useCallback, useEffect, useRef, useState } from 'react';
import type { ColumnMetadata } from '../grid/types';
import { isJsMethodSupported, type JsMethodHost, type JsMethodNotice } from './registry';

export type JsMethodNotificationState = JsMethodNotice | null;

export const useJsMethodNotifications = () => {
  const [current, setCurrent] = useState<JsMethodNotificationState>(null);
  const queueRef = useRef<JsMethodNotice[]>([]);

  const notify = useCallback((notice: JsMethodNotice) => {
    queueRef.current.push(notice);
    setCurrent((active) => active ?? queueRef.current.shift() ?? null);
  }, []);

  const dismiss = useCallback(() => {
    setCurrent(queueRef.current.shift() ?? null);
  }, []);

  return { current, dismiss, notify };
};

export const useUnsupportedJsMethodNotifications = (
  fields: ColumnMetadata[],
  host: JsMethodHost,
  notify: (notice: JsMethodNotice) => void
) => {
  const reportedMethods = useRef(new Set<string>());

  useEffect(() => {
    fields.forEach((field) => {
      const name = field.nameJsMethod?.trim();
      if (!name || isJsMethodSupported(name, host)) return;

      const fieldName = field.dataField ?? field.field ?? field.key ?? 'unknown';
      const key = `${host}:${fieldName}:${name}`;
      if (reportedMethods.current.has(key)) return;

      reportedMethods.current.add(key);
      notify({ severity: 'warning', message: `JSMethod: ${name} is not implemented.` });
    });
  }, [fields, host, notify]);
};
